import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, MapPin, Users, Crown, Calendar, Flame, ChevronRight, Plus } from 'lucide-react';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Phase 2H — Tournament listing.
 * Hyperlocal, city-first, social-warm. Defending Champions banner on every card.
 */
const FORMAT_LABEL = {
  knockout: 'Knockout',
  round_robin: 'Round-Robin League',
  league_split: 'Seasonal Split',
};

const STATUS_VISUAL = {
  registration: { label: 'Registration Open', color: '#06b6d4', accent: 'rgba(6,182,212,0.12)' },
  in_progress:  { label: 'In Progress',       color: '#f59e0b', accent: 'rgba(245,158,11,0.14)' },
  completed:    { label: 'Completed',          color: '#10b981', accent: 'rgba(16,185,129,0.12)' },
  cancelled:    { label: 'Cancelled',          color: '#64748b', accent: 'rgba(100,116,139,0.12)' },
};

export default function ArenaxTournaments() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ city: '', sport: '', status: '' });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) params.set(k, v); });
    api.get(`/arenax/tournaments?${params.toString()}`)
      .then(r => setTournaments(r.data.tournaments || []))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <ArenaxLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="arenax-tournaments">
        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="h-4 w-4 text-amber-300" />
            <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">CITY TOURNAMENTS</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-100 mb-2">
            Local leagues, recurring rituals,
            <span className="block bg-gradient-to-r from-amber-300 via-pink-400 to-cyan-300 bg-clip-text text-transparent">
              defended champions.
            </span>
          </h1>
          <p className="text-base text-slate-400 max-w-2xl">
            Hyperlocal knockouts, weekly round-robins, seasonal splits. Squad-first, community-first, no enterprise nonsense.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="arenax-tournaments-filters">
          <FilterChip value={filter.status} options={[
            { v: '', l: 'All' }, { v: 'registration', l: 'Registration Open' },
            { v: 'in_progress', l: 'In Progress' }, { v: 'completed', l: 'Completed' },
          ]} onChange={v => setFilter({ ...filter, status: v })} label="Status" />
          <FilterChip value={filter.sport} options={[
            { v: '', l: 'All Sports' }, { v: 'Football', l: 'Football' },
            { v: 'Cricket', l: 'Cricket' }, { v: 'Badminton', l: 'Badminton' },
            { v: 'Basketball', l: 'Basketball' }, { v: 'Pickleball', l: 'Pickleball' },
          ]} onChange={v => setFilter({ ...filter, sport: v })} label="Sport" />
          {user && (
            <Link to="/arenax/tournaments/new" data-testid="arenax-tournaments-create-cta"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400 hover:brightness-110 transition-all">
              <Plus className="h-3.5 w-3.5" /> Create Tournament
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl border border-slate-800 h-44 animate-pulse"
                style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }} />
            ))}
          </div>
        ) : tournaments.length === 0 ? (
          <div className="rounded-2xl border border-slate-800 p-10 text-center"
            style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
            <Trophy className="h-8 w-8 text-amber-300/40 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-100 mb-1">No tournaments here yet.</h3>
            <p className="text-sm text-slate-400">Be the captain that starts the first league in your area.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tournaments.map(t => <TournamentCard key={t.id} t={t} />)}
          </div>
        )}
      </div>
    </ArenaxLayout>
  );
}

function FilterChip({ value, options, onChange, label }) {
  return (
    <div className="relative" data-testid={`arenax-filter-${label.toLowerCase()}`}>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="appearance-none px-3 py-1.5 pr-8 rounded-lg text-xs font-bold text-slate-200 bg-slate-900 border border-slate-700 hover:border-cyan-500/40 cursor-pointer focus:outline-none focus:border-cyan-500/60">
        {options.map(o => <option key={o.v} value={o.v}>{label}: {o.l}</option>)}
      </select>
      <ChevronRight className="h-3 w-3 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
    </div>
  );
}

function TournamentCard({ t }) {
  const visual = STATUS_VISUAL[t.status] || STATUS_VISUAL.registration;
  return (
    <Link to={`/arenax/tournaments/${t.id}`}
      data-testid={`arenax-tournament-card-${t.id}`}
      className="block rounded-2xl border border-slate-800 hover:border-amber-500/30 p-5 transition-all group relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.12), var(--ax-card-bg-soft))' }}>
      <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
        style={{ color: visual.color, background: visual.accent, border: `1px solid ${visual.color}30` }}>
        {visual.label}
      </span>

      <div className="flex items-center gap-2 mb-2">
        <Trophy className="h-3.5 w-3.5 text-amber-300" />
        <span className="text-[10px] uppercase tracking-wider text-amber-300/80 font-semibold">
          {FORMAT_LABEL[t.format] || t.format}
        </span>
      </div>

      <h3 className="text-lg font-black text-slate-100 leading-tight mb-1 group-hover:text-amber-200 transition-colors">
        {t.name}
      </h3>

      {t.season_label && (
        <p className="text-[11px] text-pink-300 font-bold mb-2 uppercase tracking-wider">{t.season_label}</p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400 mb-3">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" /> {t.locality ? `${t.locality}, ` : ''}{t.city}
        </span>
        <span className="inline-flex items-center gap-1">
          <Flame className="h-3 w-3" /> {t.sport}
        </span>
        <span className="inline-flex items-center gap-1">
          <Users className="h-3 w-3" /> {t.registered_count || 0}/{t.max_squads} squads
        </span>
        {t.starts_on && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {t.starts_on}
          </span>
        )}
      </div>

      {/* Defending Champions — legacy + emotional continuity */}
      {t.defending_champion_squad_name && (
        <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2"
          data-testid={`arenax-tournament-defending-${t.id}`}>
          <Crown className="h-3.5 w-3.5 text-amber-300 shrink-0" />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-amber-300/70 font-bold">Defending Champions</div>
            <div className="text-xs font-bold text-slate-100 truncate">{t.defending_champion_squad_name}</div>
          </div>
        </div>
      )}

      {t.current_champion_squad_name && t.status === 'completed' && (
        <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2"
          data-testid={`arenax-tournament-champion-${t.id}`}>
          <Crown className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 font-bold">Champion</div>
            <div className="text-xs font-bold text-slate-100 truncate">{t.current_champion_squad_name}</div>
          </div>
        </div>
      )}
    </Link>
  );
}
